import React from 'react';
import { motion } from 'framer-motion';
import { Play, ChevronDown, Smartphone } from 'lucide-react';
export function HookScreen() {
  return <section className="min-h-screen flex flex-col items-center justify-center bg-white px-6 py-20 relative overflow-hidden" data-id="element-194">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
      backgroundImage: 'radial-gradient(circle at 1px 1px, #6B21A8 1px, transparent 0)',
      backgroundSize: '40px 40px'
    }} data-id="element-195" />

      <div className="max-w-6xl w-full mx-auto flex flex-col items-center relative z-10" data-id="element-196">
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-purple-600 font-semibold text-sm uppercase tracking-widest mb-6" data-id="element-197">
          The attention principle
        </motion.p>

        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 text-center mb-6 max-w-4xl leading-tight" data-id="element-198">
          Why most website experiences fail in the first{' '}
          <span className="text-purple-600" data-id="element-199">10 seconds</span>
        </motion.h1>

        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} className="text-lg text-slate-400 text-center mb-16 max-w-2xl" data-id="element-200">
          The difference between drop-off and engagement comes down to one
          thing: how much effort you demand before delivering value.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 w-full mb-16" data-id="element-201">
          {/* LEFT: Bad Example — AD Days website screenshot */}
          <motion.div initial={{
          opacity: 0,
          x: -40
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3,
          duration: 0.6
        }} className="flex flex-col" data-id="element-202">
            <div className="relative group" data-id="element-203">
              {/* Red glow behind */}
              <div className="absolute -inset-2 bg-red-100 rounded-3xl blur-xl opacity-50" data-id="element-204" />

              <div className="w-full bg-white border-2 border-red-200 rounded-2xl shadow-lg relative overflow-hidden" data-id="element-205">
                {/* Browser chrome */}
                <div className="flex items-center gap-1.5 px-4 py-3 bg-red-50 border-b border-red-100" data-id="element-206">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-300" data-id="element-207" />
                  <div className="w-2.5 h-2.5 rounded-full bg-red-200" data-id="element-208" />
                  <div className="w-2.5 h-2.5 rounded-full bg-red-200" data-id="element-209" />
                  <div className="ml-4 h-5 w-48 bg-red-100 rounded-full" data-id="element-210" />
                </div>

                {/* Website screenshot */}
                <div className="relative" data-id="element-211">
                  <img src="https://cdn.magicpatterns.com/uploads/8sdkvcHBrDydLGsx9L5Ubp/Screenshot_2026-04-15_at_19.28.54.png" alt="Dense AD Days website with accordion FAQs and lots of text" className="w-full h-auto object-cover object-top" style={{
                  maxHeight: '360px'
                }} data-id="element-212" />
                  {/* Subtle red overlay */}
                  <motion.div animate={{
                  opacity: [0.03, 0.08, 0.03]
                }} transition={{
                  repeat: Infinity,
                  duration: 3
                }} className="absolute inset-0 bg-red-500 pointer-events-none" data-id="element-213" />
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-center justify-center gap-3" data-id="element-214">
              <div className="w-2 h-2 rounded-full bg-red-400" data-id="element-215" />
              <span className="font-semibold text-red-600 text-base" data-id="element-216">
                High effort upfront → Drop-off
              </span>
            </div>
          </motion.div>

          {/* RIGHT: Good Example — Built-in visual mockup */}
          <motion.div initial={{
          opacity: 0,
          x: 40
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.5,
          duration: 0.6
        }} className="flex flex-col" data-id="element-217">
            <div className="relative group h-full" data-id="element-218">
              {/* Purple/green glow behind */}
              <div className="absolute -inset-2 bg-gradient-to-br from-purple-100 to-emerald-100 rounded-3xl blur-xl opacity-60" data-id="element-219" />

              <div className="block relative rounded-2xl overflow-hidden shadow-lg h-full bg-slate-900 border border-slate-800 min-h-[360px]" data-id="element-220">
                {/* Mockup Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-900/80 via-slate-900 to-emerald-900/40" data-id="element-221" />

                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" data-id="element-222" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-emerald-500/10 rounded-full blur-2xl" data-id="element-223" />

                {/* Top bar */}
                <div className="absolute top-0 left-0 right-0 p-4 flex justify-between items-center z-10" data-id="element-224">
                  <div className="flex items-center gap-2" data-id="element-225">
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center" data-id="element-226">
                      <Smartphone size={16} className="text-white/70" data-id="element-227" />
                    </div>
                    <div className="h-2 w-24 bg-white/20 rounded-full" data-id="element-228" />
                  </div>
                  <div className="h-2 w-8 bg-white/20 rounded-full" data-id="element-229" />
                </div>

                {/* Content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center z-10" data-id="element-230">
                  <h3 className="text-white/90 text-2xl font-bold mb-2 text-center px-6" data-id="element-231">
                    Animated AD Experience
                  </h3>
                  <p className="text-white/50 text-sm mb-12" data-id="element-232">
                    Interactive • Engaging • Low Effort
                  </p>

                  <div className="relative" data-id="element-233">
                    {/* Animated pulse rings */}
                    <motion.div animate={{
                    scale: [1, 1.8],
                    opacity: [0.3, 0]
                  }} transition={{
                    repeat: Infinity,
                    duration: 2.5,
                    ease: 'easeOut'
                  }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full border-2 border-white/60" data-id="element-234" />
                    <motion.div animate={{
                    scale: [1, 2.2],
                    opacity: [0.2, 0]
                  }} transition={{
                    repeat: Infinity,
                    duration: 2.5,
                    ease: 'easeOut',
                    delay: 0.5
                  }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full border-2 border-purple-300/60" data-id="element-235" />
                    <motion.div animate={{
                    scale: [1, 2.6],
                    opacity: [0.15, 0]
                  }} transition={{
                    repeat: Infinity,
                    duration: 2.5,
                    ease: 'easeOut',
                    delay: 1
                  }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full border border-white/30" data-id="element-236" />

                    {/* Play button */}
                    <motion.div animate={{
                    scale: [1, 1.06, 1]
                  }} transition={{
                    repeat: Infinity,
                    duration: 2.5,
                    ease: 'easeInOut'
                  }} className="relative w-24 h-24 rounded-full bg-gradient-to-br from-purple-600 to-purple-500 shadow-xl shadow-purple-500/30 flex items-center justify-center cursor-pointer" data-id="element-237">
                      <Play size={40} className="text-white ml-2" fill="white" data-id="element-238" />
                    </motion.div>
                  </div>
                </div>

                {/* Bottom progress bar mockup */}
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-32 h-1 bg-white/20 rounded-full overflow-hidden z-10" data-id="element-239">
                  <div className="w-1/3 h-full bg-white/80 rounded-full" data-id="element-240" />
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-center justify-center gap-3" data-id="element-241">
              <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-242" />
              <span className="font-semibold text-purple-700 text-base" data-id="element-243">
                Low effort → Instant clarity
              </span>
            </div>
          </motion.div>
        </div>

        <motion.p initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 1
      }} className="text-xl text-slate-500 text-center font-medium max-w-lg" data-id="element-244">
          People don't invest effort until they see value
        </motion.p>
      </div>

      <motion.div animate={{
      y: [0, 8, 0]
    }} transition={{
      repeat: Infinity,
      duration: 2,
      ease: 'easeInOut'
    }} className="absolute bottom-10 text-purple-300" data-id="element-245">
        <ChevronDown size={28} strokeWidth={1.5} data-id="element-246" />
      </motion.div>
    </section>;
}